// Brand constants, palette, announcer script and server endpoints. Every visible
// string here traces to docs/REBRAND_BIBLE.md — nothing derived from the source.

export const PALETTE = {
  ink: "#000000",
  bg: "#0a0a0e",
  bg2: "#14141c",
  purple: "#8b5cf6",
  purpleLt: "#c4b5fd",
  gold: "#e0922f",
  text: "#ffffff",
  muted: "#c4b5fd",
  danger: "#ff5470",
} as const;

export const FONT_DISPLAY = "Archivo, system-ui, sans-serif";
export const FONT_MONO = '"JetBrains Mono", ui-monospace, monospace';

// Difficulty tier display names (REBRAND_BIBLE §3.3). Internal keys stay the
// source-neutral normal/hard/ultimate to match difficulty.json / combat.json.
export type Difficulty = "normal" | "hard" | "ultimate";
export const TIER_LABEL: Record<Difficulty, string> = {
  normal: "BANTAM",
  hard: "ROOSTER",
  ultimate: "ALPHA",
};

// Announcer voice-line script (REBRAND_BIBLE §4). Picked at random per event.
export const ANNOUNCER = {
  matchStart: ["Roosters to the ring!", "Feathers up — here we go!", "Two birds enter. One struts out!"],
  chant: ["Ro…", "Sham…", "Bo!"],
  winThrow: ["Direct hit!", "Clean peck!", "That's a rooster's win!"],
  loseThrow: ["Ruffled!", "Pecked back!", "Ooh — feathers fly the wrong way."],
  draw: ["Standoff!", "Beak to beak — no one blinks.", "Dead heat. Go again!"],
  crow: ["CROW STRIKE!", "Let 'em hear it!"],
  ko: ["KNOCKOUT!", "Down goes the bird!", "That's all she wrote — KO!"],
  matchWin: ["You rule the roost!", "Champion of the coop!", "Winner, winner!"],
  matchLoss: ["Plucked. Better luck next crow.", "Not your day, chief.", "Back to the coop."],
} as const;

// Match structure. A VS-AI battle is one HP fight (many RPS rounds → KO).
// Online ranked defaults to best-of-3 per OWNER_DECISIONS.
export const ONLINE_BEST_OF = 3;

// Server endpoints. Overridable via ?server=host or localStorage 'rsb.server'.
// Defaults assume the game is served behind the Cloudflare Worker route.
function serverHost(): string {
  const q = new URLSearchParams(location.search).get("server");
  if (q) return q;
  try {
    const ls = localStorage.getItem("rsb.server");
    if (ls) return ls;
  } catch {
    /* ignore */
  }
  return location.host;
}

/**
 * WebSocket endpoint. The Worker routes /ws purely on QUERY PARAMS (mode /
 * room / create / reconnect — see server/src/index.ts handleWs), so callers
 * MUST pass them here; a bare /ws is treated as a fresh ranked queue entry.
 */
export function wsUrl(params: Record<string, string> = {}): string {
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  const qs = new URLSearchParams(params).toString();
  return `${proto}//${serverHost()}/ws${qs ? `?${qs}` : ""}`;
}

export function apiBase(): string {
  const proto = location.protocol === "https:" ? "https:" : "http:";
  return `${proto}//${serverHost()}`;
}

// Link back to the Chicken Arcade hub (OWNER_DECISIONS: /arcade).
export const ARCADE_URL = "/arcade";
